import * as THREE from "three";
import DomUtils from './utilities/DomUtils';
import { CustomEventTypes } from "../globals";
import { IKeybinding, Modifier, ModifierKeyCodes } from "../interfaces/Input";
import store from "../state/store";

type Rect = { x: number, y: number, width: number, height: number };

let inputBlocked = false;
const keysUpThisFrame = new Set();
const keysDownThisFrame = new Set();
const keysDown = new Set();
const modifiersDown = new Set();
let hoveredSceneCanvasBounds: Rect | undefined;

const mouseButtonDownThisFrame = new Set();
const mouseButtonUpThisFrame = new Set();
const buttonsDown = new Set();
const mousePosition = new THREE.Vector2();
let touchPosition: THREE.Vector2 | undefined = undefined;
let touchUpdates: number = 0;
let mouseWheelDelta = 0;
let touches: THREE.Vector2[] = [];
let touchScheduled = false;


let shiftKeyCount = 0;
let lastShiftKeyTime = 0;

/**
 * Handles keydown events
 * feeds the keysDownThisFrame and keysDown sets
 *
 * @param {KeyboardEvent} event
 * @return {void}
 */
function onKeyDown(event: KeyboardEvent): void
{
	if (inputBlocked) return;
	if (!keysDown.has(event.keyCode))
	{
		keysDownThisFrame.add(event.keyCode);
	}
	keysDown.add(event.keyCode);

	// TODO: remove deprecated keycode reliance https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
	if (event.keyCode === ModifierKeyCodes.SHIFT ||
		event.keyCode === ModifierKeyCodes.CTRL ||
		event.keyCode === ModifierKeyCodes.ALT ||
		(ModifierKeyCodes.COMMAND && (event.keyCode === ModifierKeyCodes.COMMAND[0] ||
			event.keyCode === ModifierKeyCodes.COMMAND[1] ||
			event.keyCode === ModifierKeyCodes.COMMAND[2])))
	{
		modifiersDown.add(event.keyCode);
	}

	detectDoubleShift(event);
}


/**
 * Detects a double shift key press and dispatches a custom event
 * not used anywhere in the codebase
 * TODO: remove if not needed
 *
 * @param {KeyboardEvent} event
 */
function detectDoubleShift(event: KeyboardEvent): void
{
	const currentTime = new Date().getTime();

	if (event.keyCode === ModifierKeyCodes.SHIFT)
	{
		shiftKeyCount++;

		if (shiftKeyCount === 2 && currentTime - lastShiftKeyTime <= 500)
		{
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onDoubleShift));
			shiftKeyCount = 0;
		}

		lastShiftKeyTime = currentTime;
	}
	else
	{
		shiftKeyCount = 0;
	}

	if (currentTime - lastShiftKeyTime > 500)
	{
		shiftKeyCount = 0;
	}
}

/**
 * Handles keyup events
 * feeds the keysUpThisFrame and removes the released key from keysDown and modifiersDown
 *
 * @param {KeyboardEvent} event
 * @return {void}
 */
function onKeyUp(event: KeyboardEvent): void
{
	if (inputBlocked) return;
	keysUpThisFrame.add(event.keyCode);
	keysDown.delete(event.keyCode);
	modifiersDown.delete(event.keyCode);
}

/**
 * Handles mouse button down events
 * feeds the mouseButtonDownThisFrame and buttonsDown sets
 *
 * @param {MouseEvent} event
 * @return {void}
 */
function onMouseButtonDown(event: MouseEvent): void
{
	if ((event as any)?.touches?.length > 0) 
	{
		onTouchStart(event as any);
		return;
	}
	if (inputBlocked) return;
	if (!buttonsDown.has(event.button) && DomUtils.getAttributeFromElementRecursive(event.target as HTMLElement, "data-acceptmouseinput") === "true")
	{
		mouseButtonDownThisFrame.add(event.button);
		buttonsDown.add(event.button);
	}
}

/**
 * Handles mouse button up events
 * feeds the mouseButtonUpThisFrame and removes the released button from buttonsDown
 *
 * @param {MouseEvent} event
 * @return {void}
 */
function onMouseButtonUp(event: MouseEvent): void
{
	if ((event as any)?.touches?.length > 0) 
	{
		onTouchEnd(event as any);
		return;
	}
	if (inputBlocked) return;
	mouseButtonUpThisFrame.add(event.button);
	buttonsDown.delete(event.button);
}

/**
 * Handles mouse move events
 * sets the mouse position and hoveredSceneCanvasBounds so we know which scene is being hovered for picking etc. purposes
 * mouse position can be retrieved with getMousePosition(), getMousePositionInRect(), and getMousePositionRaw()
 *
 * @param {MouseEvent} event
 */
function onMouseMove(event: MouseEvent): void
{
	if ((event as any)?.touches?.length > 0) 
	{
		onTouchMove(event as any);
		return;
	}
	mousePosition.set(event.clientX, event.clientY);
}

/**
 * Handles mouse wheel events
 * sets the mouseWheelDelta as 1 or -1 depending on the direction of the wheel if the mouse is over an element that accepts mouse input
 *
 * @param {WheelEvent} event
 */
function onMouseWheel(event: WheelEvent): void
{
	if (!inputBlocked && DomUtils.getAttributeFromElementRecursive(event.target as HTMLElement, "data-acceptmouseinput") === "true")
	{
		mouseWheelDelta = event.deltaY > 0 ? 1 : -1;
	}
}

function updateTouch(event: TouchEvent)
{

	touches = Array.from(event.touches).map(touch => new THREE.Vector2(touch.clientX, touch.clientY));

	if (touchScheduled) return;

	touchScheduled = true;
	requestAnimationFrame(() => 
	{
		touchGestures();
		touchScheduled = false;
	});
}

function touchGestures()
{
	if (touches.length === 1)
	{
		if (!touchPosition)
		{
			touchPosition = new THREE.Vector2();
		}
		touchPosition.set(touches[0].x, touches[0].y);
	}
	else 
	{
		touchPosition = undefined;
		if (touches.length === 2)
		{
			Input.updateZoomGesture(touches);
			Input.updatePanGesture(touches);
		}
		else 
		{
			Input.resetTouchValues();
		}
	}
	touchUpdates++;
}

function onTouchStart(event: TouchEvent): void
{
	if (event.touches.length > 1) 
	{
		event.preventDefault();
	}
	if (inputBlocked) return;
	updateTouch(event);
}

function onTouchEnd(event: TouchEvent): void
{
	updateTouch(event);
	if (event.touches.length === 0)
	{
		touchUpdates = 0;
	}
	if (inputBlocked) return;
}

function onTouchMove(event: TouchEvent): void
{
	if (event.touches.length > 1) 
	{
		event.preventDefault();
	}
	if (inputBlocked) return;
	updateTouch(event);
}

export default class Input
{
	private static areKeybindsDisabled = false;

	static init(): void
	{
		window.addEventListener('keydown', onKeyDown);
		window.addEventListener('keyup', onKeyUp);

		window.addEventListener('mousedown', onMouseButtonDown);
		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseButtonUp);
		window.addEventListener('wheel', onMouseWheel);
		window.addEventListener('touchstart', onTouchStart, { passive: false });
		window.addEventListener('touchend', onTouchEnd, { passive: false });
		window.addEventListener('touchmove', onTouchMove, { passive: false });
	}

	static updateSceneBounds(bounds?: Rect)
	{
		hoveredSceneCanvasBounds = bounds;
	}

	/**
	 * clears all event-related input data such as keys down, up, mouse position, etc.
	 * does NOT clear keybindings!
	 *
	 * @static
	 * @memberof Input
	 */
	static clearAll(): void
	{
		keysUpThisFrame.clear();
		keysDownThisFrame.clear();
		keysDown.clear();
		modifiersDown.clear();
		mouseButtonDownThisFrame.clear();
		mouseButtonUpThisFrame.clear();
		buttonsDown.clear();
		mousePosition.set(0, 0);
		touchPosition = undefined;
		mouseWheelDelta = 0;
	}

	/**
	 * returns true if the given keybind was pressed (down) this frame
	 *
	 * @static
	 * @param {IKeybinding} keybind
	 * @return {boolean}
	 * @memberof Input
	 */
	static getKeybindDown(keybind: IKeybinding): boolean
	{
		if(!keybind || this.areKeybindsDisabled) return false;

		if (keybind.modifiers.length !== modifiersDown.size) return false;

		for (const modifier of keybind.modifiers)
		{
			if (Array.isArray(modifier))
			{
				let anyModifierClicked = false;
				for (const optionalModifier of modifier)
				{
					if (this.getModifier(optionalModifier))
					{
						anyModifierClicked = true;
						break;
					}
				}
				if (!anyModifierClicked) return false;
			}
			else
			{
				if (!this.getModifier(modifier)) return false;
			}

		}
		return this.getKeyDown(keybind.keycode);
	}

	/**
	 * returns true if the given keybind is pressed (held down)
	 *
	 * @static
	 * @param {IKeybinding} keybind
	 * @return {boolean}
	 * @memberof Input
	 */
	static getKeybind(keybind: IKeybinding): boolean
	{
		if(!keybind || this.areKeybindsDisabled) return false;

		for (const modifier of keybind.modifiers)
		{
			if (Array.isArray(modifier))
			{
				let anyModifierClicked = false;
				for (const optionalModifier of modifier)
				{
					if (this.getModifier(optionalModifier))
					{
						anyModifierClicked = true;
						break;
					}
				}
				if (!anyModifierClicked) return false;
			}
			else
			{
				if (!this.getModifier(modifier)) return false;
			}
		}
		return this.getKey(keybind.keycode);
	}
	
	/**
	 * returns true if the given keybind was released (up) this frame
	 *
	 * @static
	 * @param {IKeybinding} keybind
	 * @return {boolean}
	 * @memberof Input
	 */
	static getKeybindUp(keybind: IKeybinding): boolean
	{
		if(!keybind || this.areKeybindsDisabled) return false;

		for (const modifier of keybind.modifiers)
		{
			if (Array.isArray(modifier))
			{
				let anyModifierClicked = false;
				for (const optionalModifier of modifier)
				{
					if (this.getModifier(optionalModifier))
					{
						anyModifierClicked = true;
						break;
					}
				}
				if (!anyModifierClicked) return false;
			}
			else
			{
				if (!this.getModifier(modifier)) return false;
			}
		}
		return this.getKeyUp(keybind.keycode);
	}

	/**
	 * returns true if the given single key was pressed (down) this frame
	 *
	 * @static
	 * @param {number} keyCode
	 * @return {boolean}
	 * @memberof Input
	 */
	static getKeyDown(keyCode: number): boolean
	{
		return keysDownThisFrame.has(keyCode);
	}
	
	/**
	 * returns true if the given single key is pressed (held down)
	 *
	 * @static
	 * @param {number} keyCode
	 * @return {boolean}
	 * @memberof Input
	 */
	static getKey(keyCode: number): boolean
	{
		return keysDown.has(keyCode);
	}

	/**
	 * returns true if the given single key was released (up) this frame
	 *
	 * @static
	 * @param {number} keyCode
	 * @return {boolean}
	 * @memberof Input
	 */
	static getKeyUp(keyCode: number): boolean
	{
		return keysUpThisFrame.has(keyCode);
	}
	
	/**
	 * returns true if the given modifier key is pressed (held down)
	 *
	 * @static
	 * @param {Modifier} modifier
	 * @return {boolean}
	 * @memberof Input
	 */
	static getModifier(modifier: Modifier): boolean
	{
		if (Array.isArray(modifier))
		{
			for (const keyCode of modifier)
			{
				if (keysDown.has(keyCode)) return true;
			}
			return false;
		}
		else
		{
			return keysDown.has(modifier);
		}
	}
	
	/**
	 * returns true if only the given modifier key is pressed (held down) and no other modifier key is pressed
	 *
	 * @static
	 * @param {Modifier} modifier
	 * @return {boolean}
	 * @memberof Input
	 */
	static getModifierExact(modifier: Modifier): boolean
	{
		// figure out which modifiers were NOT specified
		const givenModifier = new Set<Modifier>(new Array<Modifier>().concat(modifier));
		const allModifiers = new Array<Modifier>();
		Object.values(ModifierKeyCodes).forEach(value => allModifiers.push(value));
		const otherModifiers = allModifiers.flat().filter(mod => !givenModifier.has(mod));

		// if any of the modifiers NOT specified are also held down, we don't have an exact match
		for(const keyCode of otherModifiers)
		{
			if(keysDown.has(keyCode)) return false;
		}

		// if no actual modifier was specified, we're done here
		if(!modifier) return true;

		// if any of the modifiers specified are NOT held down, we don't have an exact match
		for(let it = givenModifier.values(), current: any = null; current = it.next();)
		{
			if(current.done) break;
			if(!keysDown.has(current.value)) return false;
		}

		return true;
	}

	/**
	 * returns true if the given mouse button was pressed (down) this frame
	 *
	 * @static
	 * @param {number} button
	 * @return {boolean}
	 * @memberof Input
	 */
	static getMouseButtonDown(button: number): boolean
	{
		return mouseButtonDownThisFrame.has(button);
	}
	
	/**
	 * returns the number of touches
	 *
	 * @static
	 * @return {*}  {number}
	 * @memberof Input
	 */
	static getTouchCount(): number
	{
		return touches.length;
	}
	
	/**
	 * returns true if the given mouse button is pressed (held down)
	 *
	 * @static
	 * @param {number} button
	 * @return {boolean}
	 * @memberof Input
	 */
	static getMouseButton(button: number): boolean
	{
		return buttonsDown.has(button);
	}

	/**
	 * returns true if the given mouse button was released (up) this frame
	 *
	 * @static
	 * @param {number} button
	 * @return {boolean}
	 * @memberof Input
	 */
	static getMouseButtonUp(button: number): boolean
	{
		return mouseButtonUpThisFrame.has(button);
	}

	/**
	 * returns the mouse position in pixels relative to the window (clientX and clientY)
	 *
	 * @static
	 * @return {THREE.Vector2} 
	 * @memberof Input
	 */
	static getMousePositionRaw(): THREE.Vector2
	{
		return mousePosition.clone();
	}

	/**
	 * returns the mouse position in normalized coordinates (0 to 1)
	 *
	 * @static
	 * @return {THREE.Vector2}
	 * @memberof Input
	 */
	static getMousePosition(): THREE.Vector2
	{
		if (hoveredSceneCanvasBounds)
		{
			return new THREE.Vector2((mousePosition.x - hoveredSceneCanvasBounds.x) / hoveredSceneCanvasBounds.width * 2 - 1,
				-(mousePosition.y - hoveredSceneCanvasBounds.y) / hoveredSceneCanvasBounds.height * 2 + 1);

		}
		return new THREE.Vector2((mousePosition.x / window.innerWidth) * 2 - 1, -((mousePosition.y / window.innerHeight) * 2 - 1));
	}

	/**
	 * returns the mouse position in normalized coordinates (0 to 1) relative to the given rect
	 *
	 * @static
	 * @param {{ x: number, y: number, width: number, height: number }} rect
	 * @return {THREE.Vector2}
	 * @memberof Input
	 */
	static getMousePositionInRect(rect: Rect): THREE.Vector2
	{
		return new THREE.Vector2((mousePosition.x - rect.x) / rect.width * 2 - 1,
			-(mousePosition.y - rect.y) / rect.height * 2 + 1);

	}

	static getTouchPositionInRect(rect: Rect): {position: THREE.Vector2, firstTouch: boolean} | undefined
	{
		if (!touchPosition) return undefined;
		return {
			position: new THREE.Vector2((touchPosition.x - rect.x) / rect.width * 2 - 1,
				-(touchPosition.y - rect.y) / rect.height * 2 + 1),
			firstTouch: touchUpdates <= 1
		};
	}

	/**
	 * returns the direction the mouse wheel was scrolled this frame
	 * 1 for down, -1 for up
	 *
	 * @static
	 * @return {number}
	 * @memberof Input
	 */
	static getMouseWheelDelta(): number
	{
		return mouseWheelDelta;
	}

	private static zoomLast: number | undefined;
	private static panStartPoint?: THREE.Vector2 = undefined;
	private static panLast?: THREE.Vector2 = undefined;
	static touchPan?: THREE.Vector2 = undefined;
	static touchPanFirst?: THREE.Vector2 = undefined;
	static touchZoom: number | undefined = undefined;
	static touchZoomCenter: THREE.Vector2 | undefined = undefined;

	static getTouchZoomCenter(): THREE.Vector2 | undefined
	{
		if (!Input.touchZoomCenter) return new THREE.Vector2(0, 0);
		if (hoveredSceneCanvasBounds)
		{
			return new THREE.Vector2((Input.touchZoomCenter.x - hoveredSceneCanvasBounds.x) / hoveredSceneCanvasBounds.width * 2 - 1,
				-(Input.touchZoomCenter.y - hoveredSceneCanvasBounds.y) / hoveredSceneCanvasBounds.height * 2 + 1);
		}
		return new THREE.Vector2((Input.touchZoomCenter.x / window.innerWidth) * 2 - 1, -((Input.touchZoomCenter.y / window.innerHeight) * 2 - 1));
	}
	

	static updateZoomGesture(touchCoords: THREE.Vector2[])
	{
		if (touchCoords.length !== 2) 
		{
			Input.zoomLast = undefined;
			Input.touchZoom = undefined;
			return;
		}
		const touchPosition1 = touchCoords[0].clone();
		const touchPosition2 = touchCoords[1].clone();
		const distance = touchPosition1.distanceTo(touchPosition2);
		Input.zoomLast ??= distance;
		const zoom = (distance - Input.zoomLast) * 0.01;
		Input.zoomLast = distance;
		if (Math.abs(zoom) < 0.02)
		{
			Input.touchZoom = 0;
			return 0
		}
		Input.touchZoom = zoom;
		Input.touchZoomCenter = touchPosition1.clone().add(touchPosition2).multiplyScalar(0.5);
		return zoom;
	}

	static updatePanGesture(touchCoords: THREE.Vector2[])
	{
		const touchPosition1 = new THREE.Vector2(touchCoords[0].x, touchCoords[0].y);
		const touchPosition2 = new THREE.Vector2(touchCoords[1].x, touchCoords[1].y);
		const midPoint = new THREE.Vector2().addVectors(touchPosition1, touchPosition2).multiplyScalar(0.5);
		Input.touchPanFirst ??= midPoint;
		if (Input.touchPanFirst.distanceTo(midPoint) < 1)
		{
			Input.touchPan = undefined;
			Input.panLast = undefined;
			return new THREE.Vector2(0, 0);
		}
		Input.panStartPoint ??= midPoint;
		Input.panLast ??= midPoint;
		const delta = midPoint.clone().sub(Input.panLast);
		delta.multiplyScalar(0.005);
		if (delta.length() < 0.01)
		{
			delta.set(0, 0);
		}
		Input.panLast = midPoint;
		Input.touchPan = delta;
		return delta;
	}

	static resetTouchValues()
	{
		Input.zoomLast = undefined;
		Input.touchZoom = undefined;
		Input.touchPan = undefined;
		Input.panStartPoint = undefined;
		Input.panLast = undefined;
		Input.touchPanFirst = undefined;
	}

	/**
	 * sends an event with the last handled input event values, then clears them all for the next frame
	 *
	 * @static
	 * @memberof Input
	 */
	static update(): void
	{
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onInputUpdated));

		keysDownThisFrame.clear();
		keysUpThisFrame.clear();
		mouseButtonDownThisFrame.clear();
		mouseButtonUpThisFrame.clear();
		mouseWheelDelta = 0;
	}

	/**
	 * blocks all input updates until release() is called
	 *
	 * @static
	 * @memberof Input
	 */
	static block(): void
	{
		inputBlocked = true;
	}

	/**
	 * resumes input updates after block() was called
	 *
	 * @static
	 * @memberof Input
	 */
	static release(): void
	{
		inputBlocked = false;
	}

	static get KeyBindings()
	{
		return store.getState().preferencesModule.editorPreferences.keybindings;
	}

	static disableKeybinds()
	{
		this.areKeybindsDisabled = true;
	}

	static enableKeybinds()
	{
		this.areKeybindsDisabled = false;
	}
}
